// Evidence that this browser actually left for the OwnerRez payment page.
//
// Path A hands the guest back to /stay/:slug/confirmed with no session_id and
// no reliable reference, so the page had nothing to check and simply asserted
// success. Anyone who opened the URL cold — a bookmark from a previous stay, a
// Back press off the abandoned payment form, a reopened tab — was told a
// payment went through, and the analytics event fired with them. The marker
// below is the missing evidence: written immediately before the redirect, read
// on the way back.
//
// sessionStorage, not localStorage, so it dies with the tab and cannot claim a
// booking next week. Every access is wrapped: a private window can throw on
// read as well as write, and a confirmation page that crashes is worse than
// one that is merely cautious.

const HANDOFF_KEY = 'cv.booking.handoff';
// Separate key, deliberately: this one only stops the funnel event firing
// twice. Folding it into the handoff record would let a counted event stand in
// as evidence of a payment on a later visit in the same tab.
const COUNTED_KEY = 'cv.booking.counted';

// Long enough for a card payment with a bank challenge in the middle, short
// enough that a tab left open overnight no longer counts as evidence.
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

function readRaw(key) {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {}
}

export function markBookingHandoff(slug) {
  writeRaw(HANDOFF_KEY, JSON.stringify({ slug, at: Date.now() }));
}

// True only if this tab left for payment for this property, recently. The
// marker survives being read, so refreshing the confirmation page does not
// downgrade a real booking to the neutral wording.
export function hasBookingHandoff(slug) {
  const raw = readRaw(HANDOFF_KEY);
  if (!raw) return false;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!parsed || parsed.slug !== slug) return false;
  if (!Number.isFinite(parsed.at) || Date.now() - parsed.at > MAX_AGE_MS) return false;
  return true;
}

// One booking is one funnel event. The caller decides whether there is
// evidence; this only makes sure a refresh, or a second look at the page later
// in the same tab, does not add to the count again.
export function claimBookingConfirmedEvent(slug) {
  if (readRaw(COUNTED_KEY) === slug) return false;
  writeRaw(COUNTED_KEY, slug);
  return true;
}
